import { Form, Formik } from "formik";
import { FileSignature, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { RichTextField } from "@/components/common/rich-text-field";
import { Button } from "@/components/ui/button";
import {
  FIELD_LABEL,
  SettingsSection,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import { updateFirmSettings } from "@/features/settings/firm-api";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";
import { textToHtml } from "@/lib/rich-text";

const schema = Yup.object({
  esignatureQuota: Yup.number()
    .typeError("Enter a number")
    .integer("Whole number")
    .min(0, "Must be 0 or more")
    .required("Required"),
  esignEmailSubject: Yup.string().max(255, "Subject is too long"),
  esignEmailBody: Yup.string().max(5000, "Body is too long"),
});

interface Values {
  esignatureQuota: string;
  esignEmailSubject: string;
  esignEmailBody: string;
}

export function ESignatureTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();

  async function handleSubmit(values: Values) {
    try {
      const updated = await updateFirmSettings({
        esignatureQuota: Number(values.esignatureQuota),
        esignEmailSubject: values.esignEmailSubject,
        esignEmailBody: values.esignEmailBody,
      });
      setSettings(updated);
      toast.success("E-signature settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="E-Signature"
        subtitle="Signature-request quota and the invitation email that pre-fills the Send dialog."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/reports/esignatures">Usage report →</Link>
          </Button>
        }
      />
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load this section. Please refresh and try again.
        </p>
      ) : (
        settings && (
          <Formik<Values>
            initialValues={{
              esignatureQuota: String(settings.esignatureQuota),
              esignEmailSubject: settings.esignEmailSubject,
              // Legacy rows hold plain text — upgrade to HTML for the editor.
              esignEmailBody: textToHtml(settings.esignEmailBody),
            }}
            enableReinitialize
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, dirty }) => (
              <Form className="flex flex-col gap-6">
                <fieldset disabled={!isAdmin} className="flex flex-col gap-6">
                  <SettingsSection title="Quota" icon={FileSignature} bodyClassName="block">
                    <FormField
                      name="esignatureQuota"
                      label="Signature request quota"
                      type="number"
                      min={0}
                      labelClassName={FIELD_LABEL}
                    />
                    <p className="text-muted-foreground mt-2 text-sm">
                      How many signature requests the firm may send. Sending is
                      blocked at zero remaining. Purchasing additional packs
                      arrives with billing in a later release.
                    </p>
                  </SettingsSection>
                  <SettingsSection title="Invitation email" icon={Mail} bodyClassName="block">
                    <p className="text-muted-foreground text-sm">
                      Pre-fills the Send dialog. Use <code>{"{link}"}</code> where
                      the recipient&apos;s signing link should appear.
                    </p>
                    <div className="mt-4 flex flex-col gap-6">
                      <FormField
                        name="esignEmailSubject"
                        label="Default subject"
                        labelClassName={FIELD_LABEL}
                      />
                      <RichTextField
                        name="esignEmailBody"
                        label="Default body"
                        labelClassName={FIELD_LABEL}
                        minHeight={140}
                        disabled={!isAdmin}
                      />
                    </div>
                  </SettingsSection>
                </fieldset>
                {!isAdmin ? (
                  <p className="text-muted-foreground text-sm">
                    Only a country director can edit e-signature settings.
                  </p>
                ) : (
                  <SettingsStickyBar>
                    <Button type="submit" disabled={isSubmitting || !dirty}>
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </SettingsStickyBar>
                )}
              </Form>
            )}
          </Formik>
        )
      )}
    </div>
  );
}
